import React, { useState, useMemo, useEffect } from 'react';
import { 
  MapPin, Calendar, Users, Filter, Search, X, ChevronRight, 
  ArrowLeft, Heart, Gift, Trophy, Sparkles, Eye,
  Play, ChevronLeft, Share2, Maximize2, Loader2
} from 'lucide-react';
import '../AcoesSociais.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AcoesSociais = () => {
  const [actionsFromAPI, setActionsFromAPI] = useState([]);
  const [loadingActions, setLoadingActions] = useState(true);
  const [error, setError] = useState(null);

  // Nomes dos meses
  const monthNames = {
    '01': 'Janeiro', '02': 'Fevereiro', '03': 'Março', '04': 'Abril',
    '05': 'Maio', '06': 'Junho', '07': 'Julho', '08': 'Agosto',
    '09': 'Setembro', '10': 'Outubro', '11': 'Novembro', '12': 'Dezembro'
  };

  // Formatar mês para exibição (sem ano)
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const parts = monthStr.split('-');
    if (parts.length >= 2) {
      return monthNames[parts[1]] || monthStr;
    }
    return monthStr;
  };

  // Buscar ações da API
  useEffect(() => {
    const fetchActions = async () => {
      try {
        setLoadingActions(true);
        setError(null);
        const response = await fetch(`${BACKEND_URL}/api/actions?status=publicado&limit=100`);
        if (response.ok) {
          const data = await response.json();
          if (data.actions) {
            setActionsFromAPI(data.actions);
          }
        } else {
          setError('Erro ao carregar ações');
        }
      } catch (err) {
        console.error('Erro ao buscar ações da API:', err);
        setError('Erro ao conectar com o servidor');
      } finally {
        setLoadingActions(false);
      }
    };
    fetchActions();
  }, []);

  // Função para obter a mídia de capa de uma ação
  // Prioridade: 1) mídia com isCover: true, 2) primeira mídia da lista
  const getCoverMedia = (action) => {
    if (!action.media || action.media.length === 0) {
      return null;
    }
    
    // Procurar mídia com isCover: true
    const coverMedia = action.media.find(m => m.isCover === true);
    if (coverMedia) {
      return coverMedia;
    }
    
    // Se não tiver, retornar a primeira
    return action.media[0];
  };

  // Função para obter a URL da imagem de capa para exibir no card
  const getCoverImageUrl = (action) => {
    const cover = getCoverMedia(action);
    if (!cover) {
      return '/placeholder-action.svg';
    }
    
    // Se for vídeo, usar thumbnail
    if (cover.type === 'video') {
      return cover.thumbnail || '/placeholder-action.svg';
    }
    
    // Se for imagem, usar url direta
    return cover.url || '/placeholder-action.svg';
  };

  // Verificar se a capa é um vídeo
  const isCoverVideo = (action) => {
    const cover = getCoverMedia(action);
    return cover?.type === 'video';
  };

  // Carregar ações da API
  const allActions = useMemo(() => {
    // Adicionar campo date formatado a partir do month
    return actionsFromAPI.map(action => ({
      ...action,
      date: formatMonth(action.month)
    }));
  }, [actionsFromAPI]);

  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

  // Função para abrir vídeo em tela cheia
  const openVideoFullscreen = (videoUrl) => {
    setIsVideoFullscreen(videoUrl);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoFullscreen = () => {
    setIsVideoFullscreen(false);
    document.body.style.overflow = 'hidden'; // mantém hidden pois o modal ainda está aberto
  };

  // Extrair regiões únicas
  const regions = useMemo(() => {
    const unique = [...new Set(allActions.map(a => a.location))];
    return ['all', ...unique];
  }, [allActions]);

  // Calcular estatísticas totais
  const stats = useMemo(() => {
    let totalBeneficiaries = 0;
    let totalActions = allActions.length;
    let totalLocations = new Set();

    allActions.forEach(action => {
      totalLocations.add(action.location);
      // Extrair números de beneficiários
      const match = action.beneficiaries?.match(/\d+/);
      if (match) totalBeneficiaries += parseInt(match[0]);
    });

    return {
      actions: totalActions,
      beneficiaries: totalBeneficiaries,
      locations: totalLocations.size,
      items: '5.000+' // estimativa de itens doados
    };
  }, [allActions]);

  // Filtrar ações
  const filteredActions = useMemo(() => {
    let filtered = allActions;

    // Filtro de região
    if (selectedRegion !== 'all') {
      filtered = filtered.filter(a => a.location === selectedRegion);
    }

    // Filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(a => 
        a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Ordenação
    if (sortBy === 'recent') {
      // Manter ordem original (mais recentes primeiro)
      filtered = [...filtered];
    } else if (sortBy === 'beneficiaries') {
      filtered = [...filtered].sort((a, b) => {
        const aNum = parseInt(a.beneficiaries?.match(/\d+/)?.[0] || 0);
        const bNum = parseInt(b.beneficiaries?.match(/\d+/)?.[0] || 0);
        return bNum - aNum;
      });
    }

    return filtered;
  }, [selectedRegion, searchTerm, sortBy, allActions]);

  // Abrir modal de ação
  const openActionModal = (action) => {
    setSelectedAction(action);
    setMediaIndex(0);
    document.body.style.overflow = 'hidden';
  };

  // Fechar modal
  const closeActionModal = () => {
    setSelectedAction(null);
    setIsImageFullscreen(false);
    document.body.style.overflow = 'auto';
  };

  // Navegação de mídia
  const nextMedia = () => {
    if (selectedAction?.media) {
      setMediaIndex((prev) => (prev + 1) % selectedAction.media.length);
    }
  };

  const prevMedia = () => {
    if (selectedAction?.media) {
      setMediaIndex((prev) => 
        prev === 0 ? selectedAction.media.length - 1 : prev - 1
      );
    }
  };

  // Extrair badge da ação
  const getActionBadge = (action) => {
    if (action.title.includes('Dia das Crianças')) return 'DIA DAS CRIANÇAS';
    if (action.title.includes('Outubro Rosa')) return 'OUTUBRO ROSA';
    if (action.title.includes('Ciclone') || action.title.includes('Reconstruindo')) return 'EMERGÊNCIA';
    if (action.title.includes('Futebol') || action.title.includes('CAFU')) return 'ESPORTE';
    if (action.title.includes('Animal')) return 'PROTEÇÃO ANIMAL';
    return 'AÇÃO SOCIAL';
  };

  return (
    <div className="acoes-sociais-page">
      {/* HERO SECTION */}
      <section className="acoes-hero">
        <div className="acoes-hero-bg"></div>
        <div className="acoes-hero-content">
          <a href="/" className="acoes-back-btn">
            <ArrowLeft size={18} />
            <span>Voltar</span>
          </a>
          
          {/* LADO ESQUERDO - TEXTO */}
          <div className="acoes-hero-text">
            <span className="acoes-hero-label">
              <Sparkles size={14} />
              Impacto Real Documentado
            </span>
            <h1>Nossas <span className="gradient-text">Ações Sociais</span></h1>
            <p>
              Cada ação que realizamos representa uma história de transformação.
              Conheça nosso trabalho com total transparência e compromisso.
            </p>
          </div>

          {/* LADO DIREITO - ESTATÍSTICAS VERTICAIS */}
          <div className="acoes-stats-grid">
            <div className="acoes-stat-card">
              <div className="acoes-stat-icon">
                <Trophy size={22} />
              </div>
              <div className="acoes-stat-content">
                <h3>{stats.actions}</h3>
                <p>Ações Realizadas</p>
              </div>
            </div>
            <div className="acoes-stat-card">
              <div className="acoes-stat-icon">
                <Heart size={22} />
              </div>
              <div className="acoes-stat-content">
                <h3>{stats.beneficiaries.toLocaleString()}+</h3>
                <p>Vidas Impactadas</p>
              </div>
            </div>
            <div className="acoes-stat-card">
              <div className="acoes-stat-icon">
                <MapPin size={22} />
              </div>
              <div className="acoes-stat-content">
                <h3>{stats.locations}</h3>
                <p>Regiões Atendidas</p>
              </div>
            </div>
            <div className="acoes-stat-card">
              <div className="acoes-stat-icon">
                <Gift size={22} />
              </div>
              <div className="acoes-stat-content">
                <h3>{stats.items}</h3>
                <p>Itens Entregues</p>
              </div>
            </div>
          </div>
        </div>

        <div
          className="charity-scroll-indicator"
          onClick={() => document.querySelector('.acoes-filters-section')?.scrollIntoView({ behavior: 'smooth' })}
          aria-label="Role para ver as ações"
          data-testid="acoes-scroll-indicator"
        >
          <span className="scroll-indicator-label">Role para descobrir</span>
          <div className="scroll-mouse" aria-hidden="true">
            <div className="scroll-mouse-wheel" />
          </div>
          <div className="scroll-indicator-arrows" aria-hidden="true">
            <span /><span /><span />
          </div>
        </div>
      </section>

      {/* FILTROS E BUSCA */}
      <section className="acoes-filters-section">
        <div className="acoes-filters-container">
          <div className="acoes-search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar ações..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}>
                <X size={18} />
              </button>
            )}
          </div>

          <button 
            className={`acoes-filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={20} />
            Filtros
          </button>

          <select 
            className="acoes-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="recent">Mais Recentes</option>
            <option value="beneficiaries">Maior Impacto</option>
          </select>
        </div>

        {showFilters && (
          <div className="acoes-filters-expanded">
            <div className="acoes-filter-group">
              <label>Região</label>
              <select 
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
              >
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region === 'all' ? 'Todas as Regiões' : region}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="acoes-results-info">
          <span>{filteredActions.length} ações encontradas</span>
        </div>
      </section>

      {/* GRID DE AÇÕES */}
      <section className="acoes-grid-section">
        {loadingActions ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
            <Loader2 size={48} className="spin" style={{ color: '#d4af37', animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#888' }}>Carregando ações...</p>
          </div>
        ) : error ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
            <p style={{ color: '#ff6b6b' }}>{error}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', background: '#d4af37', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
              Tentar novamente
            </button>
          </div>
        ) : filteredActions.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
            <Gift size={48} style={{ color: '#888' }} />
            <p style={{ color: '#888' }}>Nenhuma ação encontrada</p>
          </div>
        ) : (
        <div className="acoes-grid">
          {filteredActions.map((action, index) => (
            <div 
              key={action.id}
              className="acao-card"
              onClick={() => openActionModal(action)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`acao-card-image ${isCoverVideo(action) ? 'has-video' : ''}`}>
                <img 
                  className="acao-card-main-image"
                  src={getCoverImageUrl(action)} 
                  alt={action.title}
                  onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-action.svg'; }}
                />
                {/* Logo Viva Impact */}
                <img 
                  className="acao-card-logo"
                  src="/logo_viva_impact.png"
                  alt="Viva Impact"
                />
                {isCoverVideo(action) && (
                  <div className="acao-video-indicator">
                    <Play size={45} fill="white" color="white" strokeWidth={0} />
                  </div>
                )}
                <div className="acao-card-overlay">
                  <Eye size={24} />
                  <span>Ver Detalhes</span>
                </div>
                <span className="acao-card-badge">{getActionBadge(action)}</span>
              </div>
              
              <div className="acao-card-content">
                <div className="acao-card-meta">
                  <span className="acao-card-date">
                    <Calendar size={14} />
                    {action.date}
                  </span>
                  <span className="acao-card-location">
                    <MapPin size={14} />
                    {action.location}
                  </span>
                </div>
                
                <h3>{action.title.replace(/🎁|🤝|💙|🎉|⚽|🐶|💛|🎅/g, '').trim()}</h3>
                
                <p>{action.description.substring(0, 120)}...</p>
                
                <div className="acao-card-footer">
                  <span className="acao-card-beneficiaries">
                    <Users size={14} />
                    {action.beneficiaries}
                  </span>
                  <span className="acao-card-action">
                    Ver detalhes
                    <ChevronRight size={16} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </section>

      {/* MODAL DE DETALHES */}
      {selectedAction && (
        <div className="acao-modal-overlay" onClick={closeActionModal}>
          <div className="acao-modal" onClick={(e) => e.stopPropagation()}>
            <button className="acao-modal-close" onClick={closeActionModal}>
              <X size={24} />
            </button>

            <div className="acao-modal-content">
              {/* GALERIA DE MÍDIA */}
              <div className="acao-modal-gallery">
                <div className="acao-gallery-main">
                  {selectedAction.media?.[mediaIndex]?.type === 'video' ? (
                    <div className="acao-video-container">
                      {selectedAction.media[mediaIndex].url.includes('drive.google.com') ? (
                        <iframe
                          className="acao-modal-video"
                          src={selectedAction.media[mediaIndex].url}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                          allowFullScreen
                          style={{border: 'none', width: '100%', height: '70vh', maxHeight: '600px'}}
                        />
                      ) : (
                        <video
                          controls
                          autoPlay
                          className="acao-modal-video"
                          src={selectedAction.media[mediaIndex].url}
                        />
                      )}
                      <button 
                        className="acao-fullscreen-btn"
                        onClick={() => openVideoFullscreen(selectedAction.media[mediaIndex].url)}
                        title="Tela Cheia"
                      >
                        <Maximize2 size={24} />
                        <span>Tela Cheia</span>
                      </button>
                    </div>
                  ) : (
                    <img
                      src={selectedAction.media?.[mediaIndex]?.url}
                      alt=""
                      className="acao-modal-image"
                      onClick={() => setIsImageFullscreen(true)}
                    />
                  )}
                  
                  {selectedAction.media?.length > 1 && (
                    <>
                      <button className="acao-gallery-nav prev" onClick={prevMedia}>
                        <ChevronLeft size={32} />
                      </button>
                      <button className="acao-gallery-nav next" onClick={nextMedia}>
                        <ChevronRight size={32} />
                      </button>
                      <div className="acao-gallery-counter">
                        {mediaIndex + 1} / {selectedAction.media.length}
                      </div>
                    </>
                  )}
                </div>

                {/* THUMBNAILS */}
                {selectedAction.media?.length > 1 && (
                  <div className="acao-gallery-thumbs">
                    {selectedAction.media.map((media, index) => (
                      <div
                        key={index}
                        className={`acao-thumb ${mediaIndex === index ? 'active' : ''}`}
                        onClick={() => setMediaIndex(index)}
                      >
                        {media.type === 'video' ? (
                          <div className="acao-thumb-video">
                            <Play size={20} />
                          </div>
                        ) : (
                          <img src={media.thumbnail || media.url} alt="" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* INFORMAÇÕES */}
              <div className="acao-modal-info">
                <div className="acao-modal-header">
                  <span className="acao-modal-badge">
                    {getActionBadge(selectedAction)}
                  </span>
                  <h2 className="acao-modal-title">{selectedAction.title.replace(/🎁|🤝|💙|🎉|⚽|🐶|💛|🎅/g, '').trim()}</h2>
                  <p className="acao-modal-subtitle">{selectedAction.subtitle}</p>
                </div>

                <div className="acao-modal-meta">
                  <div className="acao-modal-meta-item">
                    <Calendar size={18} />
                    <div>
                      <span className="label">Data</span>
                      <span className="value">{selectedAction.date}</span>
                    </div>
                  </div>
                  <div className="acao-modal-meta-item">
                    <MapPin size={18} />
                    <div>
                      <span className="label">Local</span>
                      <span className="value">{selectedAction.location}</span>
                    </div>
                  </div>
                  <div className="acao-modal-meta-item">
                    <Users size={18} />
                    <div>
                      <span className="label">Beneficiários</span>
                      <span className="value">{selectedAction.beneficiaries}</span>
                    </div>
                  </div>
                </div>

                <div className="acao-modal-description">
                  <h3>Sobre a Ação</h3>
                  {selectedAction.description.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>

                {selectedAction.tags && (
                  <div className="acao-modal-tags">
                    <h3>Tags</h3>
                    <div className="acao-tags-list">
                      {selectedAction.tags.map((tag, index) => (
                        <span key={index} className="acao-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="acao-modal-actions">
                  <button className="btn-modal-primary">
                    <Heart size={18} />
                    Apoiar Causa
                  </button>
                  <button className="btn-modal-secondary">
                    <Share2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FULLSCREEN IMAGE */}
      {isImageFullscreen && selectedAction && (
        <div className="acao-fullscreen-overlay" onClick={() => setIsImageFullscreen(false)}>
          <button className="acao-fullscreen-close">
            <X size={32} />
          </button>
          <img
            src={selectedAction.media?.[mediaIndex]?.url}
            alt=""
          />
        </div>
      )}

      {/* FULLSCREEN VIDEO */}
      {isVideoFullscreen && (
        <div className="acao-fullscreen-overlay video-fullscreen" onClick={closeVideoFullscreen}>
          <button className="acao-fullscreen-close" onClick={closeVideoFullscreen}>
            <X size={32} />
          </button>
          <div className="acao-fullscreen-video-container" onClick={(e) => e.stopPropagation()}>
            {isVideoFullscreen.includes('drive.google.com') ? (
              <iframe
                src={isVideoFullscreen}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                style={{border: 'none', width: '100%', height: '100%'}}
              />
            ) : (
              <video
                controls
                autoPlay
                src={isVideoFullscreen}
                style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AcoesSociais;
